import { useEffect, useState } from "react";
import { formatDate, formatNumber } from "../../lib/format";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { tmApi, type TmActivityEntityType, type TmActivityView, type TmUserOption } from "../../api/tm";
import Ic from "../../components/icons.jsx";
import { Select } from "../../components/Select";

const FILTERS: { key: TmActivityEntityType | "ALL"; labelKey: string; labelDefault: string }[] = [
  { key: "ALL",     labelKey: "tm.activity.filter.all",      labelDefault: "All" },
  { key: "TASK",    labelKey: "tm.activity.filter.tasks",    labelDefault: "Tasks" },
  { key: "PROJECT", labelKey: "tm.activity.filter.projects", labelDefault: "Projects" },
  { key: "MEETING", labelKey: "tm.activity.filter.meetings", labelDefault: "Meetings" },
  { key: "COMMENT", labelKey: "tm.activity.filter.comments", labelDefault: "Comments" },
];

const PAGE_SIZE = 50;

/**
 * Activity log viewer — read-only stream of every TM action (task created,
 * status changed, comment added, project member added, …). Useful for both
 * audit ("who moved this task to BLOCKED?") and at-a-glance recap of what the
 * team did this morning.
 *
 * Filters on entity type and paginates server-side; we don't try to load
 * everything client-side because activity grows unbounded.
 */
export default function TmActivityPage() {
  const { t } = useTranslation();
  const [entityType, setEntityType] = useState<TmActivityEntityType | "ALL">("ALL");
  const [actorId, setActorId] = useState<string>("");
  const [users, setUsers] = useState<TmUserOption[]>([]);
  const [page, setPage] = useState(0);
  const [items, setItems] = useState<TmActivityView[] | null>(null);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => { void tmApi.users().then(setUsers).catch(() => setUsers([])); }, []);

  useEffect(() => {
    setLoading(true);
    tmApi
      .listActivity({
        entityType: entityType === "ALL" ? undefined : entityType,
        actorId: actorId || undefined,
        page,
        size: PAGE_SIZE,
      })
      .then((p) => {
        setItems(p.content);
        setTotalElements(p.totalElements);
        setTotalPages(p.totalPages);
      })
      .catch(() => {
        setItems([]);
        setTotalElements(0);
        setTotalPages(0);
      })
      .finally(() => setLoading(false));
  }, [entityType, actorId, page]);

  useEffect(() => { setPage(0); }, [entityType, actorId]);

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <h1 style={{ margin: 0, fontSize: 24, color: "var(--ink)" }}>{t("tm.activity.title", "Activity")}</h1>
        <div style={{ fontSize: 13, color: "var(--ink-3)", marginTop: 4 }}>
          {t("tm.activity.subtitle", { defaultValue: "{{n}} events in the team timeline", n: formatNumber(totalElements) })}
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <div style={chipBar}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setEntityType(f.key)}
              style={{
                background: entityType === f.key ? "var(--brand-primary)" : "var(--surface)",
                color: entityType === f.key ? "var(--on-primary)" : "var(--ink-2)",
                border: `1px solid ${entityType === f.key ? "var(--brand-primary)" : "var(--border)"}`,
                padding: "6px 12px",
                borderRadius: 999,
                fontSize: 12.5,
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              {t(f.labelKey, f.labelDefault)}
            </button>
          ))}
        </div>
        <Select<string>
          value={actorId}
          onChange={setActorId}
          ariaLabel={t("tm.activity.filterByUser", "Filter by user")}
          style={{ minWidth: 180 }}
          options={[
            { value: "", label: t("tm.activity.allUsers", "All users") },
            ...users.map((u) => ({ value: u.id, label: u.name })),
          ]}
        />
      </div>

      {loading && items === null ? (
        <div style={{ color: "var(--ink-3)" }}>{t("common.loading", "Loading…")}</div>
      ) : !items || items.length === 0 ? (
        <div style={emptyState}>
          <div style={{ fontSize: 28, marginBottom: 4 }}>📋</div>
          <b style={{ color: "var(--ink-2)" }}>{t("tm.activity.empty.title", "No activity yet.")}</b>
          <div style={{ color: "var(--ink-3)", fontSize: 13, marginTop: 4 }}>
            {t("tm.activity.empty.hint", "Every task, project and meeting change will show up here once team members start working.")}
          </div>
        </div>
      ) : (
        <>
          <ul style={feedList}>
            {items.map((evt) => (
              <li key={evt.id} style={feedItem}>
                <span style={{ ...iconBadge, ...iconStyleFor(evt.entityType) }}>
                  {iconFor(evt.entityType)}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, color: "var(--ink)" }}>
                    <b>{evt.actorName ?? t("tm.activity.systemActor", "System")}</b>{" "}
                    <span style={{ color: "var(--ink-3)" }}>
                      {actionPhrase(evt.action, evt.entityType)}
                    </span>{" "}
                    <Link to={linkFor(evt)} style={{ color: "var(--brand-primary)", textDecoration: "none" }}>
                      {entityShortId(evt.entityId)}
                    </Link>
                  </div>
                  {evt.changes && Object.keys(evt.changes).length > 0 && (
                    <ChangeDiff changes={evt.changes} />
                  )}
                </div>
                <span style={{ fontSize: 11.5, color: "var(--ink-4)", whiteSpace: "nowrap" }}>
                  {relTime(evt.createdAt, t)}
                </span>
              </li>
            ))}
          </ul>
          {totalPages > 1 && (
            <div style={pageBar}>
              <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
                {t("tm.activity.pageOf", { defaultValue: "Page {{page}} of {{total}}", page: page + 1, total: totalPages })}
              </span>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  type="button"
                  className="btn sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  {t("tm.activity.newer", "← Newer")}
                </button>
                <button
                  type="button"
                  className="btn sm"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {t("tm.activity.older", "Older →")}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ChangeDiff({ changes }: { changes: Record<string, unknown> }) {
  const entries = Object.entries(changes).slice(0, 6);
  return (
    <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 4 }}>
      {entries.map(([k, v]) => (
        <span key={k} style={diffChip} title={`${k}: ${stringify(v)}`}>
          <b style={{ color: "var(--ink-3)" }}>{k}</b>
          <span style={{ color: "var(--ink-2)" }}>{stringify(v)}</span>
        </span>
      ))}
    </div>
  );
}

function stringify(v: unknown): string {
  if (v == null) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function iconFor(t: TmActivityEntityType): React.ReactNode {
  switch (t) {
    case "TASK":    return <Ic.check />;
    case "PROJECT": return <Ic.book />;
    case "MEETING": return <Ic.calendar />;
    case "COMMENT": return <Ic.send />;
  }
}

function iconStyleFor(t: TmActivityEntityType): React.CSSProperties {
  switch (t) {
    case "TASK":    return { background: "var(--info-bg)", color: "var(--info)" };
    case "PROJECT": return { background: "var(--tone-violet-bg)", color: "var(--tone-violet)" };
    case "MEETING": return { background: "var(--warn-bg)", color: "var(--warn)" };
    case "COMMENT": return { background: "var(--surface-muted)", color: "var(--ink-2)" };
  }
}

function actionPhrase(action: string, entityType: TmActivityEntityType): string {
  // action keys come from TmActivityLogger (e.g. "TASK_CREATED", "STATUS_CHANGED").
  // Render in plain English; specific BE strings can land here later.
  const a = action.toLowerCase().replace(/_/g, " ");
  const entityNoun = entityType.toLowerCase();
  // Common case: action already names the entity → don't repeat.
  if (a.includes(entityNoun)) return a;
  return `${a} ${entityNoun}`;
}

function entityShortId(id: string): string {
  return id.split("-")[0];
}

function linkFor(evt: TmActivityView): string {
  switch (evt.entityType) {
    case "TASK":    return `/tm/tasks/${evt.entityId}`;
    case "PROJECT": return `/tm/projects/${evt.entityId}`;
    case "MEETING": return `/tm/meetings/${evt.entityId}`;
    case "COMMENT": return `/tm/tasks/${evt.entityId}`;
  }
}

function relTime(iso: string, t: TFunction): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return t("tm.activity.relTime.justNow", "just now");
  if (min < 60) return t("tm.activity.relTime.minutes", { defaultValue: "{{n}}m ago", n: min });
  const h = Math.floor(min / 60);
  if (h < 24) return t("tm.activity.relTime.hours", { defaultValue: "{{n}}h ago", n: h });
  const d = Math.floor(h / 24);
  if (d < 7) return t("tm.activity.relTime.days", { defaultValue: "{{n}}d ago", n: d });
  return formatDate(iso);
}

const chipBar: React.CSSProperties = { display: "flex", gap: 8, flexWrap: "wrap" };
const feedList: React.CSSProperties = { listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 };
const feedItem: React.CSSProperties = { display: "flex", alignItems: "flex-start", gap: 12, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px" };
const iconBadge: React.CSSProperties = { width: 28, height: 28, borderRadius: 8, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 };
const diffChip: React.CSSProperties = { background: "var(--surface-2)", border: "1px solid var(--border)", padding: "1px 6px", borderRadius: 4, fontSize: 11, display: "inline-flex", gap: 4, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const emptyState: React.CSSProperties = { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, padding: 32, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" };
const pageBar: React.CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 };
